/**
 * MicrosoftAuth.js — Microsoft Entra ID OAuth redirect handler for the AdminPortal web flow.
 *
 * Handles the web-browser Microsoft OAuth flow:
 *   1. initiateMicrosoftLogin() — opens the server authorize route, which mints browser-bound state,
 *      then redirects to backend /oauth/microsoft/authorize endpoint
 *   2. handleMicrosoftCallback(state, onSuccess) — validates state, fetches JWT from backend,
 *      calls onSuccess(jwtToken, user)
 *
 * Auth flow:
 *   User click → initiateMicrosoftLogin() → backend /oauth/microsoft/authorize
 *   → login.microsoftonline.com/common/oauth2/v2.0/authorize
 *   → Microsoft login → backend /oauth/microsoft/callback → backend stores JWT keyed by state
 *   → backend redirects to web UI with ?microsoft_state={state}
 *   → handleMicrosoftCallback(state, onSuccess) → GET /oauth/microsoft/token?state={state} → JWT
 */

/**
 * Initiates the Microsoft OAuth login flow for the web browser.
 *
 * Opens the backend authorize route, which mints and binds state,
 * then redirects the browser to the backend's /oauth/microsoft/authorize endpoint.
 * The backend stores the client_redirect_uri, then redirects to Microsoft OAuth.
 *
 * @returns {void}
 */
export function initiateMicrosoftLogin() {
    // Build the backend authorize URL — backend handles the rest of the OAuth flow
    const apiBase = getApiBase();
    const clientRedirectUri = encodeURIComponent(`${window.location.origin}/login`);
    const authorizeUrl = `${apiBase}/oauth/microsoft/authorize?client_redirect_uri=${clientRedirectUri}`;

    window.location.assign(authorizeUrl);
}

/**
 * Handles the Microsoft OAuth callback after the backend redirects back to the web UI.
 *
 * Redeems the browser-bound state returned by the backend,
 * retrieving the JWT from the backend via GET /oauth/microsoft/token?state={state},
 * then calls onSuccess(jwtToken, user).
 *
 * @param {string} state - The state parameter returned from the backend redirect
 * @param {function} onSuccess - Callback invoked with (jwtToken, user) on success
 * @returns {Promise<void>}
 * @throws {Error} When state is invalid/missing, or backend returns an error
 */
export async function handleMicrosoftCallback(state, onSuccess) {
    if (!state) throw new Error('Microsoft OAuth completion code is missing.');

    const apiBase = getApiBase();
    const tokenUrl = `${apiBase}/oauth/microsoft/token?state=${encodeURIComponent(state)}`;

    const response = await fetch(tokenUrl, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Microsoft OAuth token retrieval failed (${response.status}): ${errorText}`);
    }

    const data = await response.json();

    if (onSuccess) {
        onSuccess(data.JwtToken, data.User);
    }
}

/**
 * Returns the API base URL for the Microsoft OAuth endpoints.
 * The /oauth/microsoft/* endpoints live on the API project (zoomapi-*.meetingsync.ai),
 * NOT the Functions project (func-*.meetingsync.ai).
 *
 * @returns {string} Base URL, e.g. "https://zoomapi-test.qolsync.ai"
 */
function getApiBase() {
    return process.env.REACT_APP_ZOOM_API_URL || 'https://zoomapi-test.qolsync.ai';
}
